import type { FetchData } from '@hooks/useDataFetcher';
import {
  buildCacheKey,
  getCache,
  invalidateCache,
  setCache,
} from '@shared/util/cache-utils';
import { useCallback, useState } from 'react';
import type {
  IFinancingCreateOffer,
  IFinancingFinance,
  IFinancingFinanceType,
  IFinancingOffer,
  IFinancingOfferConstraints,
  IFinancingOfferTemplate,
  IFinancingProductOffer,
  IFinancingStatus,
  IFinancingTerm,
} from 'types/custom';

import type { SnackbarActions } from './useSnackbar';

interface ICollection<T> {
  path: string;
  list: Array<T>;
  loading: boolean;
}

interface IState {
  terms: ICollection<IFinancingTerm>;
  finances: ICollection<IFinancingFinance>;
  templates: ICollection<IFinancingOfferTemplate>;
  financeTypes: ICollection<IFinancingFinanceType>;
}

export type UseFinancingOffers = IState & {
  loading: boolean;
  getTerms: () => Promise<IFinancingTerm[]>;
  getFinances: () => Promise<IFinancingFinance[]>;
  archiveFinanceOffer: (id: string) => Promise<boolean>;
  deleteTerm: (obj: IFinancingTerm) => Promise<boolean>;
  getFinanceTypes: () => Promise<IFinancingFinanceType[]>;
  deleteFinance: (obj: IFinancingFinance) => Promise<boolean>;
  deleteFinanceType: (obj: IFinancingFinanceType) => Promise<boolean>;
  saveTerm: (obj: IFinancingTerm) => Promise<IFinancingTerm | undefined>;
  getFinancingOffersByMarket: (
    market: string,
    customerTypes?: string[],
    status?: IFinancingStatus,
  ) => Promise<IFinancingOffer[]>;
  archiveProductOffer: (market: string, offerId: string) => Promise<boolean>;
  getFinanceById: (id: string, market?: string) => Promise<IFinancingFinance>;
  getProductOffers: (
    productId: string,
    market: string,
  ) => Promise<IFinancingProductOffer[]>;
  createProductOffer: (
    market: string,
    obj: IFinancingCreateOffer,
  ) => Promise<IFinancingProductOffer[] | undefined>;
  saveFinance: (
    obj: IFinancingFinance,
  ) => Promise<IFinancingFinance | undefined>;
  saveFinanceType: (
    obj: IFinancingFinanceType,
  ) => Promise<IFinancingFinanceType | undefined>;
  getFinanceTemplates: (
    financeId: string,
  ) => Promise<IFinancingOfferTemplate[]>;
  activateTemplate: (
    templateId: string,
    financeId: string,
    activeInMarkets: string[],
  ) => Promise<void>;
  saveFinanceTemplate: (
    financeId: string,
    obj: IFinancingOfferTemplate,
  ) => Promise<IFinancingOfferTemplate | undefined>;
  deleteFinanceTemplate: (
    financeId: string,
    obj: IFinancingOfferTemplate,
  ) => Promise<boolean>;
  getFinancingOfferConstraints: (
    id: string,
  ) => Promise<IFinancingOfferConstraints | null>;
};

const API_PREFIX = '/api/financing-offers/admin';
const pendingMarketOffers = new Map<string, Promise<IFinancingOffer[]>>();

const invalidateMarketOffers = () => {
  invalidateCache((key) => key.startsWith('getFinancingOffersByMarket|'));
  pendingMarketOffers.clear();
};

export const useFinancingOffers = (
  makeRequest: FetchData,
  snackbar: SnackbarActions,
): UseFinancingOffers => {
  const [fetchCount, setFetchCount] = useState(0);
  const [state, setState] = useState<IState>({
    terms: { list: [], loading: false, path: '/' },
    finances: { list: [], loading: false, path: '/' },
    templates: { list: [], loading: false, path: '/' },
    financeTypes: { list: [], loading: false, path: '/' },
  });

  const GET = useCallback(
    async <T = any>(path: string, key?: keyof IState): Promise<T[]> => {
      try {
        // If no key provided, just make the API call without state management
        if (!key) {
          setFetchCount((count) => count + 1);
          const res = await makeRequest<any>(`${API_PREFIX}/${path}`);
          setFetchCount((count) => count - 1);
          return res?.data ?? [];
        }

        setState((state) => {
          const val = state[key];

          if (val.path === path && val.list.length > 0) {
            return state;
          }

          return {
            ...state,
            [key]: { ...val, loading: true },
          };
        });

        // Get current state value for the cache check
        const currentVal = await new Promise<ICollection<T>>((resolve) => {
          setState((prevState) => {
            resolve(prevState[key] as ICollection<T>);
            return prevState;
          });
        });

        if (currentVal.path === path && currentVal.list.length > 0) {
          return currentVal.list as Array<T>;
        }

        const res = await makeRequest<any>(`${API_PREFIX}/${path}`);
        const list = res?.data?.[key] ?? [];

        setState((state) => ({
          ...state,
          [key]: { list, path, loading: false },
        }));

        return list;
      } catch (err) {
        if (key) {
          setState((state) => ({
            ...state,
            [key]: { ...state[key], loading: false },
          }));
        } else {
          setFetchCount((count) => count - 1);
        }
        throw err;
      }
    },
    [makeRequest],
  );

  const DELETE = useCallback<(path: string) => Promise<boolean>>(
    async (path) => {
      setFetchCount((count) => count + 1);

      try {
        const res = await makeRequest<any>(
          `${API_PREFIX}/${path}`,
          undefined,
          'DELETE',
        );

        if (res.successful === false) {
          throw new Error(res.message);
        }

        return true;
      } catch (err: any) {
        snackbar.error(err?.message || 'Request failed', err);
        return false;
      } finally {
        setFetchCount((count) => count - 1);
      }
    },
    [makeRequest, snackbar],
  );

  const getFinancingOffersByMarket = useCallback<
    UseFinancingOffers['getFinancingOffersByMarket']
  >(
    async (market, customerTypes, status) => {
      const key = buildCacheKey('getFinancingOffersByMarket', {
        market,
        status,
        customerTypes: [...(customerTypes ?? [])].sort().join(','),
      });
      const cached = getCache(key) as IFinancingOffer[] | undefined;
      if (cached !== undefined) {
        return cached;
      }
      const pending = pendingMarketOffers.get(key);
      if (pending) {
        return pending;
      }

      const query = [
        customerTypes?.length ? `customerType=${customerTypes.join(',')}` : '',
        status ? `status=${status}` : '',
      ]
        .filter(Boolean)
        .join('&');

      const request = GET<IFinancingOffer>(
        `markets/${market}/financing-offers${query ? `?${query}` : ''}`,
      )
        .then((offers) => {
          if (pendingMarketOffers.get(key) === request) {
            setCache(key, offers);
          }
          return offers;
        })
        .finally(() => {
          if (pendingMarketOffers.get(key) === request) {
            pendingMarketOffers.delete(key);
          }
        });
      pendingMarketOffers.set(key, request);
      return request;
    },
    [GET],
  );

  const POST = useCallback(
    async <T = any>(
      path: string,
      body?: any,
      dataExtractor: (res: any) => T = (res) => res.data,
    ): Promise<T | undefined> => {
      setFetchCount((count) => count + 1);

      try {
        const res = await makeRequest<any>(
          `${API_PREFIX}/${path}`,
          undefined,
          'POST',
          body,
        );

        if (res.successful === false) {
          throw new Error(res.message);
        }

        return dataExtractor(res) as T;
      } catch (err: any) {
        snackbar.error(err?.message || 'Request failed', err);
      } finally {
        setFetchCount((count) => count - 1);
      }
    },
    [makeRequest, snackbar],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency subset
  const saveData = useCallback(
    async <T = any>(
      path: string,
      data: T,
      key: keyof IState,
      dataIndex: keyof T = 'id' as keyof T,
    ): Promise<T | undefined> => {
      setState((state) => ({
        ...state,
        [key]: { ...state[key], loading: true },
      }));

      try {
        const res = await makeRequest<{
          data?: T;
          message?: string;
        }>(
          !data[dataIndex]
            ? `${API_PREFIX}/${path}`
            : `${API_PREFIX}/${path}/${data[dataIndex]}`,
          undefined,
          !data[dataIndex] ? 'POST' : 'PUT',
          data,
        );

        if (!res?.data) {
          throw new Error(res?.message ?? 'No data');
        }

        setState((state) => ({
          ...state,
          [key]: {
            ...state[key],
            list: !data[dataIndex]
              ? [res.data, ...state[key].list]
              : state[key].list.map((item) =>
                  (item as T)[dataIndex] !== res.data![dataIndex]
                    ? item
                    : res.data,
                ),
          },
        }));

        return res.data;
      } catch (err: any) {
        snackbar.error(err?.message || 'Failed to save item');
      } finally {
        setState((state) => ({
          ...state,
          [key]: { ...state[key], loading: false },
        }));
      }
    },
    [makeRequest],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency subset
  const deleteData = useCallback(
    async <T = any>(
      key: keyof IState,
      path: string,
      data: T,
      dataIndex: keyof T,
    ): Promise<boolean> => {
      setState((state) => ({
        ...state,
        [key]: { ...state[key], loading: true },
      }));

      try {
        const res = await makeRequest<{
          message?: string;
          successful?: boolean;
        }>(`${API_PREFIX}/${path}/${data[dataIndex]}`, undefined, 'DELETE');

        if (res?.successful === false) {
          throw new Error(res?.message);
        }

        setState((state) => ({
          ...state,
          [key]: {
            ...state[key],
            list: state[key].list.filter(
              (item) => (item as T)[dataIndex] !== data[dataIndex],
            ),
          },
        }));

        return true;
      } catch (err: any) {
        snackbar.error(err?.message || 'Failed to delete item');

        return false;
      } finally {
        setState((state) => ({
          ...state,
          [key]: { ...state[key], loading: false },
        }));
      }
    },
    [makeRequest],
  );

  const fetchDataById = useCallback(
    async <T = any>(path: string): Promise<T> => {
      setFetchCount((count) => count + 1);

      try {
        return makeRequest<{ data?: T; message?: string }>(
          `${API_PREFIX}/${path}`,
        ) as Promise<T>;
      } catch (err) {
        throw err;
      } finally {
        setFetchCount((count) => count - 1);
      }
    },
    [makeRequest],
  );

  // Custom methods

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency subset
  const activateTemplate = useCallback<UseFinancingOffers['activateTemplate']>(
    async (templateId, financeId, activeInMarkets) => {
      if (templateId && activeInMarkets.length > 0) {
        setFetchCount((count) => count + 1);

        try {
          const res = await makeRequest<any>(
            `${API_PREFIX}/financing-offer-templates/${templateId}/activate?financeId=${financeId}`,
            undefined,
            'POST',
            activeInMarkets,
          );

          setState((state) => ({
            ...state,
            templates: {
              ...state.templates,
              list: state.templates.list.map((template) => ({
                ...template,
                activeInMarkets:
                  template.id === templateId
                    ? activeInMarkets
                    : template.activeInMarkets,
              })),
            },
          }));

          if (res?.successful === false) {
            throw new Error(res?.message);
          }
          invalidateMarketOffers();
        } catch (err: any) {
          snackbar.error(err.message, err);
        } finally {
          setFetchCount((count) => count - 1);
        }
      }
    },
    [makeRequest],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency subset
  const archiveFinanceOffer = useCallback<
    UseFinancingOffers['archiveFinanceOffer']
  >(
    async (financingOfferId) => {
      setFetchCount((count) => count + 1);

      try {
        const res = await makeRequest<any>(
          `${API_PREFIX}/financing-offers/${financingOfferId}/archive`,
          undefined,
          'POST',
        );

        if (res?.successful === false) {
          throw new Error(res?.message);
        }

        invalidateMarketOffers();
        return true;
      } catch (err: any) {
        snackbar.error(err.message, err);
        return false;
      } finally {
        setFetchCount((count) => count - 1);
      }
    },
    [makeRequest],
  );

  return {
    ...state,
    loading: fetchCount > 0,

    // Finances
    getFinances: () => GET('finances', 'finances'),
    saveFinance: (obj) => saveData('finances', obj, 'finances'),
    deleteFinance: (obj) => deleteData('finances', 'finances', obj, 'id'),
    getFinanceById: (id, market) =>
      fetchDataById(`finances/${id}${market ? `?market=${market}` : ''}`),

    // Financing offers
    archiveFinanceOffer,
    getFinancingOffersByMarket,

    // Terms
    getTerms: () => GET('terms', 'terms'),
    saveTerm: (obj) => saveData('terms', obj, 'terms'),
    deleteTerm: (obj) => deleteData('terms', 'terms', obj, 'id'),

    // Types
    getFinanceTypes: () => GET('finance-types', 'financeTypes'),
    saveFinanceType: (obj) => saveData('finance-types', obj, 'financeTypes'),
    deleteFinanceType: (obj) =>
      deleteData('financeTypes', 'finance-types', obj, 'id'),

    // Templates
    activateTemplate,
    getFinanceTemplates: (financeId) =>
      GET(`finances/${financeId}/financing-offer-templates`, 'templates'),
    saveFinanceTemplate: (financeId, obj) =>
      saveData(
        `finances/${financeId}/financing-offer-templates`,
        obj,
        'templates',
      ),
    deleteFinanceTemplate: (financeId, obj) =>
      deleteData(
        'templates',
        `finances/${financeId}/financing-offer-templates`,
        obj,
        'id',
      ),

    // Product offers
    archiveProductOffer: (market, offerId) =>
      DELETE(`markets/${market}/offers/${offerId}`),
    getProductOffers: (productId, market) =>
      GET(`markets/${market}/offers?productId=${productId}`),
    createProductOffer: (market, body) =>
      POST(`markets/${market}/offers`, body, (res) => res.data.createdOffers),

    // Constraints (non-admin endpoint)
    getFinancingOfferConstraints: async (id: string) => {
      setFetchCount((count) => count + 1);
      try {
        const res = await makeRequest<any>(
          `/api/financing-offers/${id}/constraints`,
        );
        return res?.data ?? null;
      } finally {
        setFetchCount((count) => count - 1);
      }
    },
  };
};
